import React from 'react';
import { observer } from 'mobx-react-lite';
import { IonIcon, IonText } from '@ionic/react';
import { trophy, medal } from 'ionicons/icons';
import { ContestParticipationStatus } from '../../../hooks/useContestParticipation';
import Icon from '../../../components/icons/Icon';
import './ContestBanner.css';

interface ContestBannerProps {
  contestStatus: ContestParticipationStatus & {
    isCompleted?: boolean;
    bestScore?: number;
    scores?: number[];
    userEntry?: any;
  };
  isSelected?: boolean;
  onSelect?: () => void;
}

const ContestBanner = observer(
  ({ contestStatus, isSelected = false, onSelect }: ContestBannerProps) => {
    if (!contestStatus.isParticipating || !contestStatus.contest) {
      return null;
    }

    const isCompleted = contestStatus.runCount >= contestStatus.maxRuns;
    const bestScore = (contestStatus as any).bestScore || 0;
    const scores = (contestStatus as any).scores || [];
    const hasScores = scores.length > 0;

    const getPositionText = (position: number | null) => {
      if (!position) return 'Unranked';

      switch (position) {
        case 1:
          return '1st Place';
        case 2:
          return '2nd Place';
        case 3:
          return '3rd Place';
        default:
          return `${position}th Place`;
      }
    };

    const getPositionIcon = (position: number | null) => {
      if (!position) return null;

      switch (position) {
        case 1:
          return <IonIcon icon={trophy} className="position-icon gold" />;
        case 2:
          return <IonIcon icon={medal} className="position-icon silver" />;
        case 3:
          return <IonIcon icon={medal} className="position-icon bronze" />;
        default:
          return <span className="position-number">{position}</span>;
      }
    };

    const handleClick = () => {
      if (onSelect) {
        onSelect();
      }
    };

    const bannerClasses = [
      'contest-banner',
      isSelected ? 'selected' : 'unselected',
      isCompleted ? 'completed' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // CSS-Klassen für RallySign basierend auf Status
    const rallySignClasses = [
      'rally-sign',
      isSelected ? 'rally-sign-selected' : 'rally-sign-unselected',
      isCompleted ? 'rally-sign-completed' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const contentClasses = [
      'contest-banner-content',
      isSelected ? 'selected-content' : 'unselected-content',
      isCompleted ? 'completed-content' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={bannerClasses}
        onClick={handleClick}
        style={{ cursor: onSelect ? 'pointer' : 'default' }}
      >
        {/* Rally-Schild mit Glow- und Farbeffekten */}
        <Icon
          name="RallySign"
          className={rallySignClasses}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '240px',
            height: 'auto',
            zIndex: 1,
            transition: 'all 0.3s ease',
          }}
        />
        <div className={contentClasses}>
          <div className="contest-info">
            <div className="contest-name">
              {scores.length > 0 && (
                <span className="last-score">
                  Last: {scores[scores.length - 1].toFixed(1)}
                </span>
              )}
              <IonText>{contestStatus.contestName}</IonText>
              {hasScores && (
                <span className="best-score">Best: {bestScore.toFixed(1)}</span>
              )}
            </div>
            <div className="contest-stats">
              <div className="position-display">
                {getPositionIcon(contestStatus.position)}
                <span className="position-text">
                  {getPositionText(contestStatus.position)}
                </span>
              </div>

              {/* Status-Indikatoren zwischen contest-stats und run-counter */}
              <div className="status-indicators">
                {isSelected && !isCompleted && (
                  <span className="selection-indicator">Active</span>
                )}
                {isCompleted && (
                  <span className="completion-indicator">Complete</span>
                )}
                {!isSelected && !isCompleted && (
                  <span className="selection-indicator">Inactive</span>
                )}
              </div>

              <div className="run-counter">
                <span className="run-count">
                  {isCompleted ? (
                    <>
                      ✅ {contestStatus.runCount}/{contestStatus.maxRuns}{' '}
                      Complete
                    </>
                  ) : (
                    <>
                      Run {contestStatus.runCount + 1} of{' '}
                      {contestStatus.maxRuns}
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default ContestBanner;
